/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Type-aware value rendering.
 *
 * Values render by type, never raw: booleans as Enabled/Disabled, timestamps as formatted dates,
 * objects and arrays as something a person can scan. A renderer that prints `[object Object]` has
 * failed the one job the detail panel exists for.
 */

import { DateTime } from 'luxon'

import { getUserTimezone } from '@/insights/common/timezone'

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/

export interface FormattedValue {
  text: string
  /** True when the value is a structure worth offering behind a disclosure. */
  expandable: boolean
  /** Pretty-printed JSON, present only when `expandable`. */
  raw?: string
}

const MAX_INLINE_LENGTH = 120

/**
 * Render a diff value for display.
 *
 * Diff values come from JSON, so the cases below are exhaustive in practice; anything else still
 * produces a readable string rather than throwing.
 */
export const formatValue = (value: unknown): FormattedValue => {
  if (value === null || value === undefined) return { text: '—', expandable: false }
  if (typeof value === 'boolean') return { text: value ? 'Enabled' : 'Disabled', expandable: false }
  if (typeof value === 'number') return { text: String(value), expandable: false }

  if (typeof value === 'string') {
    if (value === '') return { text: 'Empty', expandable: false }
    if (ISO_DATE.test(value)) {
      const dt = DateTime.fromISO(value, { zone: 'utc' }).setZone(getUserTimezone())
      if (dt.isValid) return { text: dt.toFormat('dd LLL yyyy, hh:mm a'), expandable: false }
    }
    return value.length > MAX_INLINE_LENGTH
      ? { text: `${value.slice(0, MAX_INLINE_LENGTH - 1)}…`, expandable: true, raw: value }
      : { text: value, expandable: false }
  }

  if (Array.isArray(value)) {
    return {
      text: `${value.length} item${value.length === 1 ? '' : 's'}`,
      expandable: value.length > 0,
      raw: safeJson(value),
    }
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value as object)
    return {
      text: keys.length === 0 ? 'Empty' : `${keys.length} field${keys.length === 1 ? '' : 's'}`,
      expandable: keys.length > 0,
      raw: safeJson(value),
    }
  }

  return { text: String(value as string), expandable: false }
}

/** Never let a circular or exotic value break the panel. */
export const safeJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? String(value)
  } catch {
    return String(value)
  }
}

/** The one-line form, for compact contexts like the setpoint table. */
export const formatValueText = (value: unknown): string => formatValue(value).text

export interface NumericDelta {
  /** The rendered magnitude with its arrow, e.g. `▼ 2.0 °C`. Never signed — see below. */
  text: string
  /** The same thing spelled out, for screen readers that would skip the arrow. */
  label: string
  direction: 'up' | 'down'
}

/**
 * The magnitude of a change between two numeric values, e.g. `▼ 2.0 °C`. Returns null when either
 * side is not a number, or when they are equal.
 *
 * The arrow carries the direction, so the magnitude is deliberately unsigned: these values are
 * often temperatures, where a leading `−` reads as part of the number rather than as "decreased by".
 * `direction` is returned separately so callers style and announce it without parsing the string.
 */
export const numericDelta = (before: unknown, after: unknown, unit = ''): NumericDelta | null => {
  if (typeof before !== 'number' || typeof after !== 'number' || before === after) return null

  const diff = after - before
  const magnitude = Math.abs(diff)
  const rendered = `${Number.isInteger(magnitude) ? String(magnitude) : magnitude.toFixed(1)}${unit ? ` ${unit}` : ''}`

  return {
    text: `${diff < 0 ? '▼' : '▲'} ${rendered}`,
    label: `${diff < 0 ? 'Decreased' : 'Increased'} by ${rendered}`,
    direction: diff < 0 ? 'down' : 'up',
  }
}
